import { setMcpActionSelection } from "./McpActionPicker.utils";
import { createPortal } from "react-dom";
import { useLayoutEffect, type CSSProperties } from "react";
import {
  AudioLines,
  Brain,
  Boxes,
  Check,
  ChevronDown,
  FileText,
  ImagePlus,
  LoaderCircle,
  SlidersHorizontal,
  Sparkles,
  Square,
  Target,
  TriangleAlert,
  Wrench,
  X,
} from "lucide-react";
import type { GenerationParameters, ThinkingLevel } from "@lumen/shared";
import { AgentIcon } from "../agent-workspaces/AgentIcon";
import { t } from "../../i18n";
import { Toggle } from "../Toggle";
import { RichTextInput } from "./RichTextInput";
import { ActivitySettingsPanel } from "./ActivitySettingsPanel";
import { ComposerQuickParameterBar } from "./ComposerQuickParameterBar";
import { ToolPopup } from "./ComposerToolPopup";
import {
  activityDetails,
  activityDescription,
  activityDisplayLabel,
} from "./ComposerActivityUtils";
import { formats } from "./ComposerFormats";
import { anchorComposerPopup } from "./ComposerPopupPosition";
import type { ComposerViewProps } from "./ComposerContextTypes";

export function ComposerView(props: ComposerViewProps) {
  const activeOptionId =
    props.slashOpen && props.skillMatches[props.skillIndex]
      ? `composer-skill-${props.skillMatches[props.skillIndex].id}`
      : props.artifactOpen && props.referenceMatches[props.artifactIndex]
        ? `composer-reference-${props.artifactIndex}`
        : undefined;
  useLayoutEffect(() => {
    if (activeOptionId)
      document
        .getElementById(activeOptionId)
        ?.scrollIntoView({ block: "nearest" });
  }, [activeOptionId]);
  return (
    <div
      data-chat-composer={props.mode === "send" ? "true" : undefined}
      className={`w-full max-w-full min-w-0 ${props.mode === "edit" ? "composer-edit" : ""} ${props.mode === "send" ? "sm:mx-auto sm:max-w-3xl sm:px-4 sm:pb-5" : ""}`}
    >
      <div className="max-w-full overflow-visible rounded-none border-x-0 border-t border-b-0 border-stone-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,.08)] focus-within:border-lumen-500 sm:rounded-2xl sm:border dark:border-white/10 dark:bg-[#1b201e]">
        {props.files.length > 0 && (
          <div className="flex gap-2 border-b border-stone-100 p-3 dark:border-white/10">
            {props.files.map((file) => (
              <div key={file.id} className="group relative">
                {file.mimeType.startsWith("image/") ? (
                  <img
                    src={file.url}
                    alt={file.name}
                    className="size-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-16 max-w-56 items-center gap-2 rounded-lg bg-stone-100 px-3 text-xs dark:bg-white/5">
                    <AudioLines size={18} className="shrink-0" />
                    <span className="truncate">{file.name}</span>
                  </div>
                )}
                <button
                  onClick={() =>
                    props.setFiles((current) =>
                      current.filter((item) => item.id !== file.id),
                    )
                  }
                  className="absolute -top-1 -right-1 rounded-full bg-stone-900 p-1 text-white"
                >
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
        {props.repositoryDiagnostics.length > 0 && (
          <div
            role="alert"
            className="flex items-start gap-2 border-b border-stone-100 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-white/10 dark:bg-amber-950/40 dark:text-amber-200"
          >
            <TriangleAlert size={14} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">
                {t("Repository context problems ({{number}})", {
                  number: props.repositoryDiagnostics.length,
                })}
              </p>
              <ul className="mt-0.5 list-inside list-disc">
                {props.repositoryDiagnostics.slice(0, 3).map((diagnostic) => (
                  <li key={diagnostic} className="truncate">
                    {diagnostic}
                  </li>
                ))}
                {props.repositoryDiagnostics.length > 3 && (
                  <li>
                    {t("{{number}} more…", {
                      number: props.repositoryDiagnostics.length - 3,
                    })}
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
        <div
          className="relative max-w-full min-w-0"
          style={{ height: props.editorHeight }}
        >
          <RichTextInput
            ref={props.richInput}
            initialValue={props.initialValue}
            placeholder={props.placeholder}
            onChange={props.updateValue}
            onSelectionChange={props.onSelectionChange}
            mentions={props.mentionCatalog}
            listboxId={
              props.slashOpen
                ? "composer-skill-list"
                : props.artifactOpen
                  ? "composer-artifact-list"
                  : undefined
            }
            activeOptionId={activeOptionId}
            onKeyDown={props.keydown}
            onPaste={props.paste}
            onHeightChange={(height) =>
              props.setEditorHeight(Math.max(96, Math.min(256, height)))
            }
          />
        </div>
        <ComposerQuickParameterBar
          activity={props.activity}
          model={props.selectedActivityModel}
          supportedParameters={
            props.activity === "ask"
              ? props.supportedParameters
              : (props.selectedActivityModel?.supportedParameters ?? [])
          }
          availableVoices={props.availableVoices}
          values={props.composerParameterValues}
          onChange={props.updateComposerParameter}
          compact
        />
        {props.slashOpen &&
          props.skillMenuPosition &&
          createPortal(
            <div
              id="composer-skill-list"
              role="listbox"
              aria-label={t("Available skills")}
              style={props.skillMenuPosition}
              className="fixed z-50 max-h-64 overflow-y-auto rounded-xl border border-stone-200 bg-white p-1 shadow-xl dark:border-white/10 dark:bg-[#222725]"
            >
              {props.skillsStatus === "loading" ? (
                <p className="px-3 py-2 text-sm text-stone-400">
                  {t("Loading skills…")}
                </p>
              ) : props.skillsStatus === "error" ? (
                <p className="px-3 py-2 text-sm text-red-600 dark:text-red-300">
                  {t("Skills could not be loaded.")}
                </p>
              ) : props.skillMatches.length === 0 ? (
                <p className="px-3 py-2 text-sm text-stone-400">
                  {t("No matching skills.")}
                </p>
              ) : (
                props.skillMatches.map((skill, index) => (
                  <button
                    id={`composer-skill-${skill.id}`}
                    role="option"
                    aria-selected={index === props.skillIndex}
                    key={skill.id}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => props.chooseSkill(skill)}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left ${index === props.skillIndex ? "bg-lumen-50 dark:bg-lumen-900/30" : "hover:bg-stone-50 dark:hover:bg-white/5"}`}
                  >
                    <Sparkles size={14} className="shrink-0 text-lumen-600" />
                    <span className="min-w-0">
                      <code className="text-sm font-bold">/{skill.slug}</code>
                      <span className="ml-2 text-xs text-stone-400">
                        {skill.description || skill.name}
                      </span>
                      {"source" in skill && skill.source === "repository" && (
                        <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                          {skill.kind === "command"
                            ? "OpenCode command"
                            : "Repository"}
                        </span>
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>,
            document.body,
          )}
        {props.artifactOpen &&
          props.artifactMenuPosition &&
          createPortal(
            <div
              id="composer-artifact-list"
              role="listbox"
              aria-label={t("Available artifacts")}
              style={props.artifactMenuPosition}
              className="fixed z-50 max-h-72 overflow-y-auto rounded-xl border border-stone-200 bg-white p-1 shadow-xl dark:border-white/10 dark:bg-[#222725]"
            >
              {props.artifactsStatus === "loading" ||
              props.workspaceFilesStatus === "loading" ? (
                <p className="px-3 py-2 text-sm text-stone-400">
                  {t("Loading references…")}
                </p>
              ) : props.artifactsStatus === "error" &&
                props.workspaceFilesStatus === "error" ? (
                <p className="px-3 py-2 text-sm text-red-600 dark:text-red-300">
                  {t("References could not be loaded.")}
                </p>
              ) : props.referenceMatches.length === 0 ? (
                <p className="px-3 py-2 text-sm text-stone-400">
                  {t("No matching references.")}
                </p>
              ) : (
                props.referenceMatches.map((reference, index) => (
                  <button
                    id={`composer-reference-${index}`}
                    role="option"
                    aria-selected={index === props.artifactIndex}
                    key={
                      reference.kind === "artifact"
                        ? reference.item.id
                        : reference.item.reference
                    }
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() =>
                      reference.kind === "artifact"
                        ? props.chooseArtifact(reference.item)
                        : props.chooseWorkspaceFile(reference.item)
                    }
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left ${index === props.artifactIndex ? "bg-lumen-50 dark:bg-lumen-900/30" : "hover:bg-stone-50 dark:hover:bg-white/5"}`}
                  >
                    {reference.kind === "artifact" &&
                    reference.item.mimeType.startsWith("image/") &&
                    reference.item.mimeType !== "image/svg+xml" ? (
                      <img
                        src={reference.item.url}
                        alt=""
                        className="size-9 shrink-0 rounded-md object-cover"
                      />
                    ) : (
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-lumen-50 text-lumen-700 dark:bg-lumen-900/30 dark:text-lumen-300">
                        <FileText size={15} />
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">
                        {reference.kind === "artifact"
                          ? reference.item.name
                          : reference.item.path}
                      </span>
                      <span className="block truncate text-[11px] text-stone-400">
                        {reference.kind === "artifact"
                          ? `${reference.item.mimeType} · ${reference.item.reference}`
                          : `${reference.item.repositoryName ?? reference.item.workspaceName} · ${reference.item.reference}`}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>,
            document.body,
          )}
        {props.activity === "ask" && (
          <div
            className="flex items-center gap-0.5 border-t border-stone-100 px-3 py-1 dark:border-white/10"
            aria-label={t("Markdown formatting")}
          >
            {formats.map(({ kind, label, icon: Icon }) => (
              <button
                key={kind}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => props.applyFormat(kind)}
                className="button p-1.5 text-stone-500"
                title={label}
                aria-label={label}
              >
                <Icon size={15} />
              </button>
            ))}
          </div>
        )}
        <div className="composer-toolbar relative flex min-w-0 items-start gap-1.5 border-t border-stone-100 px-2.5 py-2 dark:border-white/10">
          <input
            ref={props.fileInput}
            type="file"
            accept={
              props.activity === "transcription"
                ? "audio/*,video/*"
                : props.acceptsTripoModelFile
                  ? ".glb,.gltf,.fbx,.obj,.stl,model/*"
                  : "image/*,.docx,.pptx,.xlsx,.pdf"
            }
            multiple
            className="hidden"
            onChange={(event) =>
              void props.upload([...(event.target.files ?? [])])
            }
          />
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {props.mode === "send" && props.onActivityChange && (
              <div ref={props.activityPicker} className="relative shrink-0">
                <button
                  type="button"
                  disabled={props.disabled}
                  onClick={() => props.setActivityOpen((open) => !open)}
                  className={`button h-9 rounded-lg ${props.activityOpen ? "gap-1.5 bg-stone-100 px-2.5 dark:bg-white/10" : "w-9 p-0"}`}
                  aria-haspopup="menu"
                  aria-expanded={props.activityOpen}
                  aria-label={props.activityLabel}
                  title={props.activityLabel}
                >
                  {(() => {
                    const Icon =
                      activityDetails[
                        props.activity as keyof typeof activityDetails
                      ].icon;
                    return <Icon size={16} />;
                  })()}
                  {props.activityOpen && (
                    <>
                      <span className="text-xs font-semibold">
                        {props.activityLabel}
                      </span>
                      <ChevronDown size={13} />
                    </>
                  )}
                </button>
                {props.activityOpen && (
                  <div className="absolute bottom-full left-0 z-40 mb-2 w-72 max-w-[calc(100vw-1rem)] rounded-xl border border-stone-200 bg-white p-1.5 shadow-xl dark:border-white/10 dark:bg-[#222725]">
                    {(
                      [
                        "ask",
                        "image",
                        "audio",
                        "video",
                        "speech",
                        "transcription",
                        "model3d",
                        "sprite",
                        "tileset",
                      ] as const
                    ).map((item) => {
                      const details =
                        activityDetails[item as keyof typeof activityDetails];
                      const Icon = details.icon;
                      const available =
                        props.availableActivities.includes(item);
                      return (
                        <button
                          key={item}
                          type="button"
                          disabled={!available}
                          onClick={() => {
                            props.onActivityChange?.(item);
                            props.setActivityOpen(false);
                          }}
                          className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left ${props.activity === item ? "bg-lumen-50 dark:bg-lumen-900/30" : "hover:bg-stone-50 disabled:opacity-45 dark:hover:bg-white/5"}`}
                        >
                          <Icon
                            size={17}
                            className="mt-0.5 shrink-0 text-lumen-600"
                          />
                          <span className="min-w-0 flex-1">
                            <strong className="block text-sm">
                              {activityDisplayLabel(item)}
                            </strong>
                            <span className="block text-xs text-stone-400">
                              {available
                                ? activityDescription(item)
                                : t("No compatible model is available")}
                            </span>
                          </span>
                          {props.activity === item && (
                            <Check
                              size={15}
                              className="mt-0.5 text-lumen-600"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {props.activity === "ask" && (
              <div ref={props.agentPicker} className="relative shrink-0">
                <button
                  type="button"
                  disabled={props.disabled || props.agents.length === 0}
                  onClick={() => {
                    props.setAgentOpen((open) => !open);
                    props.setToolsOpen(false);
                    props.setThinkingOpen(false);
                  }}
                  className={`button h-9 max-w-32 gap-1.5 rounded-lg px-2 sm:max-w-40 ${props.agentOpen ? "bg-stone-100 dark:bg-white/10" : ""}`}
                  title={`Agent: ${props.selectedAgent?.name || t("None")}`}
                  aria-label={`Agent: ${props.selectedAgent?.name || t("None")}`}
                  aria-expanded={props.agentOpen}
                >
                  <AgentIcon
                    name={props.selectedAgent?.icon}
                    size={16}
                    className="shrink-0"
                  />
                  <span className="hidden min-w-0 truncate text-xs min-[420px]:inline">
                    {props.selectedAgent?.name || "Agent"}
                  </span>
                  <ChevronDown
                    size={13}
                    className="hidden shrink-0 min-[420px]:block"
                  />
                </button>
                {props.agentOpen && (
                  <div className="absolute bottom-full left-0 z-40 mb-2 w-72 max-w-[calc(100vw-1rem)] rounded-xl border border-stone-200 bg-white p-1.5 shadow-xl dark:border-white/10 dark:bg-[#222725]">
                    <p className="px-2 pt-1 pb-1.5 text-[11px] font-bold tracking-wider text-stone-400 uppercase">
                      {t("Choose agent")}
                    </p>
                    {props.agents.map((agent) => (
                      <button
                        key={agent.id}
                        type="button"
                        onClick={() => {
                          props.onAgentChange(agent.id);
                          props.setAgentOpen(false);
                        }}
                        className={`flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left ${agent.id === props.agentId ? "bg-lumen-50 dark:bg-lumen-900/30" : "hover:bg-stone-50 dark:hover:bg-white/5"}`}
                      >
                        <AgentIcon
                          name={agent.icon}
                          size={15}
                          className={`mt-0.5 shrink-0 ${agent.id === props.agentId ? "text-lumen-600" : "text-stone-400"}`}
                        />
                        <span className="min-w-0 flex-1">
                          <strong className="block truncate text-sm">
                            {agent.name}
                          </strong>
                          {agent.description && (
                            <span className="mt-0.5 line-clamp-2 block text-xs leading-4 text-stone-400">
                              {agent.description}
                            </span>
                          )}
                        </span>
                        {agent.id === props.agentId && (
                          <Check
                            size={14}
                            className="mt-0.5 shrink-0 text-lumen-600"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {props.activity !== "speech" && (
              <button
                disabled={props.uploading}
                onClick={() => props.fileInput.current?.click()}
                className="button size-9 shrink-0 rounded-lg p-0"
                title={
                  props.activity === "transcription"
                    ? t("Attach audio or video")
                    : props.acceptsTripoModelFile
                      ? t("Attach a 3D model file")
                      : t("Attach props.files")
                }
                aria-label={
                  props.activity === "transcription"
                    ? t("Attach audio or video")
                    : props.acceptsTripoModelFile
                      ? t("Attach a 3D model file")
                      : t("Attach props.files")
                }
              >
                {props.uploading ? (
                  <LoaderCircle className="animate-spin" size={17} />
                ) : (
                  <ImagePlus size={17} />
                )}
              </button>
            )}
            {props.activity === "ask" &&
              (props.tools.length > 0 ||
                props.mcpServers.length > 0 ||
                Object.values(props.activityModels).some(
                  (models) => models.length > 0,
                )) && (
                <button
                  type="button"
                  onClick={() => {
                    props.setPanelSection("tools");
                    props.setToolsOpen(
                      props.toolsOpen && props.panelSection === "tools"
                        ? false
                        : true,
                    );
                    props.setThinkingOpen(false);
                    props.setSettingsToolId(null);
                    props.setMcpDetailServerId(null);
                  }}
                  onPointerDown={(event) =>
                    anchorComposerPopup(
                      event.currentTarget,
                      props.toolButton,
                      props.setToolMenuPosition,
                      544,
                    )
                  }
                  className={`button relative size-9 shrink-0 rounded-lg p-0 ${props.toolsOpen && props.panelSection === "tools" ? "bg-stone-100 dark:bg-white/10" : ""}`}
                  title={t("Tools and MCP")}
                  aria-label={t("Tools and MCP")}
                >
                  <Wrench size={17} />
                  {props.activeToolCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-violet-600 px-1 text-[9px] text-white">
                      {props.activeToolCount}
                    </span>
                  )}
                </button>
              )}
            {props.activity === "ask" && props.agentWorkspaces.length > 0 && (
              <button
                type="button"
                onPointerDown={(event) =>
                  anchorComposerPopup(
                    event.currentTarget,
                    props.toolButton,
                    props.setToolMenuPosition,
                    544,
                  )
                }
                onClick={() => {
                  props.setPanelSection("environment");
                  props.setToolsOpen(
                    props.toolsOpen && props.panelSection === "environment"
                      ? false
                      : true,
                  );
                  props.setThinkingOpen(false);
                  props.setSettingsToolId(null);
                  props.setMcpDetailServerId(null);
                }}
                className={`button relative h-11 min-w-0 shrink rounded-lg px-2 sm:px-2.5 ${props.toolsOpen && props.panelSection === "environment" ? "bg-stone-100 dark:bg-white/10" : ""}`}
                title={
                  props.selectedWorkspaceLabel
                    ? `Environment: ${props.selectedWorkspaceLabel}${props.selectedRepositoryLabel ? ` · ${props.selectedRepositoryLabel}` : ""}`
                    : t("Agent environment")
                }
                aria-label={
                  props.selectedWorkspaceLabel
                    ? `Environment: ${props.selectedWorkspaceLabel}`
                    : t("Agent environment")
                }
              >
                <Boxes size={17} className="shrink-0" />
                {props.selectedWorkspaceLabel && (
                  <span className="absolute top-1 right-1 size-1.5 rounded-full bg-amber-500 md:hidden" />
                )}
                {props.selectedWorkspaceLabel && (
                  <span className="hidden min-w-0 text-left md:block">
                    <span className="block max-w-28 truncate text-xs">
                      {props.selectedWorkspaceLabel}
                    </span>
                    {props.selectedRepositoryLabel && (
                      <span className="block max-w-28 truncate text-[10px] font-normal text-stone-400">
                        {props.selectedRepositoryLabel}
                      </span>
                    )}
                  </span>
                )}
              </button>
            )}
            {props.activity === "ask" && props.supportsThinking && (
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    props.setThinkingOpen((open) => !open);
                    props.setToolsOpen(false);
                  }}
                  className={`button relative size-9 overflow-hidden rounded-lg p-0 ${props.thinkingLevel ? "border border-stone-200 dark:border-white/10" : ""} ${props.thinkingOpen ? "bg-stone-100 dark:bg-white/10" : ""}`}
                  title={`Thinking: ${props.thinkingLevel || "default"}`}
                  aria-label={`Thinking level: ${props.thinkingLevel || "default"}`}
                  aria-expanded={props.thinkingOpen}
                >
                  {props.thinkingLevel && (
                    <span
                      className="pointer-events-none absolute inset-x-0 bottom-0 bg-lumen-200 transition-[height] dark:bg-lumen-700/60"
                      style={{ height: props.thinkingFill }}
                    />
                  )}
                  <Brain
                    size={18}
                    className="relative z-10 text-stone-600 dark:text-stone-200"
                  />
                  <span className="sr-only">
                    {t("Brain indicator filled to {{percent}}", {
                      percent: props.thinkingFill,
                    })}
                  </span>
                </button>
                {props.thinkingOpen && (
                  <div className="absolute right-0 bottom-full z-40 mb-2 w-36 rounded-xl border border-stone-200 bg-white p-1 shadow-xl dark:border-white/10 dark:bg-[#222725]">
                    {(
                      ["", ...props.thinkingLevels] as Array<ThinkingLevel | "">
                    ).map((level) => (
                      <button
                        key={level || "default"}
                        type="button"
                        onClick={() => {
                          props.onThinkingLevelChange(level);
                          props.setThinkingOpen(false);
                        }}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs ${props.thinkingLevel === level ? "bg-lumen-50 text-lumen-700 dark:bg-lumen-900/30 dark:text-lumen-300" : "hover:bg-stone-50 dark:hover:bg-white/5"}`}
                      >
                        <span className="capitalize">{level || "Default"}</span>
                        {props.thinkingLevel === level && <Check size={13} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {(props.activity === "ask" ||
              props.advancedParameterFields.length > 0) && (
              <button
                type="button"
                ref={props.paramsButton}
                onPointerDown={(event) =>
                  anchorComposerPopup(
                    event.currentTarget,
                    props.paramsButton,
                    props.setParamsMenuPosition,
                    320,
                  )
                }
                onClick={() => {
                  props.setParamsOpen((open) => !open);
                  props.setThinkingOpen(false);
                  props.setToolsOpen(false);
                }}
                className={`button relative size-9 shrink-0 rounded-lg p-0 ${props.paramsOpen ? "bg-stone-100 dark:bg-white/10" : ""}`}
                title={
                  props.activity === "ask"
                    ? t("Options for this message")
                    : t("Model options")
                }
                aria-label={
                  props.activity === "ask" ? t("Options") : t("Model options")
                }
                aria-expanded={props.paramsOpen}
              >
                <SlidersHorizontal size={17} />
                {props.activeParameterCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-lumen-600 px-1 text-[9px] text-white">
                    {props.activeParameterCount}
                  </span>
                )}
              </button>
            )}
            {props.activity === "ask" && (
              <button
                type="button"
                onClick={() =>
                  props.setGenerationParameters((current) => ({
                    ...current,
                    ...(current.goal ? { goal: undefined } : { goal: true }),
                  }))
                }
                className={`button relative h-9 gap-1.5 rounded-lg px-2 text-xs ${props.generationParameters.goal ? "bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300" : ""}`}
                title={t(
                  "Use this message as a goal with a verifiable outcome",
                )}
                aria-label={t("Goal mode")}
                aria-pressed={props.generationParameters.goal === true}
              >
                <Target size={17} aria-hidden="true" />
                {t("Goal")}
              </button>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {props.mode === "edit" && (
              <button
                type="button"
                onClick={props.onCancel}
                disabled={props.disabled}
                className="button h-9 shrink-0 px-3 text-xs"
              >
                {t("Cancel")}
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                props.generating && !props.value.trim() && !props.files.length
                  ? props.onStop?.()
                  : void props.submit()
              }
              disabled={
                props.generating
                  ? false
                  : props.disabled ||
                    props.uploading ||
                    (!props.value.trim() && !props.files.length)
              }
              className={`button-primary h-9 shrink-0 ${props.mode === "edit" ? "gap-1.5 px-3 text-xs" : "w-9 p-0"}`}
              aria-label={
                props.generating && !props.value.trim() && !props.files.length
                  ? t("Stop")
                  : props.mode === "edit"
                    ? (props.editLabel ?? t("Save as new version"))
                    : props.generating
                      ? t("Queue message")
                      : props.activity === "image"
                        ? t("Create image")
                        : props.activity === "audio"
                          ? t("Generate audio")
                          : props.activity === "video"
                            ? t("Create video")
                            : props.activity === "speech"
                              ? t("Generate speech")
                              : props.activity === "transcription"
                                ? t("Transcribe")
                                : t("Send")
              }
            >
              {props.generating &&
              !props.value.trim() &&
              !props.files.length ? (
                <Square size={16} fill="currentColor" />
              ) : props.mode === "edit" ? (
                <>
                  <Check size={16} /> {t("Save")}
                </>
              ) : (
                (() => {
                  const Icon =
                    activityDetails[
                      props.activity as keyof typeof activityDetails
                    ].icon;
                  return <Icon size={16} />;
                })()
              )}
            </button>
          </div>
        </div>
        {props.toolsOpen &&
          props.toolMenuPosition &&
          createPortal(
            <ToolPopup
              section={props.panelSection}
              ref={props.toolPopup}
              position={props.toolMenuPosition}
              tools={props.tools}
              toolIds={props.toolIds}
              activityModels={props.activityModels}
              activityModelIds={props.activityModelIds}
              enabledActivities={props.enabledActivities}
              activitySettings={props.activitySettings}
              activityDetail={props.activityDetail}
              onActivitySettingsChange={
                props.onActivitySettingsChange ?? (() => undefined)
              }
              onOpenActivity={props.setActivityDetail}
              onActivityModelChange={
                props.onActivityModelChange ?? (() => undefined)
              }
              onToggleActivity={props.onToggleActivity ?? (() => undefined)}
              mcpServers={props.mcpServers}
              mcpServerIds={props.mcpServerIds}
              mcpDetailServerId={props.mcpDetailServerId}
              mcpTools={props.mcpTools}
              mcpToolSelections={props.mcpToolSelections}
              agentWorkspaces={props.agentWorkspaces}
              agentWorkspaceIds={props.agentWorkspaceIds}
              agents={props.agents}
              agentId={props.agentId}
              repositories={props.repositories}
              repositoryIds={props.repositoryIds}
              supportsThinking={props.supportsThinking}
              thinkingLevel={props.thinkingLevel}
              settingsToolId={props.settingsToolId}
              saveStatus={props.toolSaveStatus}
              onToggle={(toolId) =>
                props.setToolIds((current) =>
                  current.includes(toolId)
                    ? current.filter((id) => id !== toolId)
                    : [...current, toolId],
                )
              }
              onToggleMcp={(serverId) =>
                props.setMcpServerIds((current) =>
                  current.includes(serverId)
                    ? current.filter((id) => id !== serverId)
                    : [...current, serverId],
                )
              }
              onOpenMcp={(serverId) => {
                props.setMcpDetailServerId(serverId);
                if (serverId) void props.loadMcpTools(serverId);
              }}
              onSetMcpTools={(serverId, names, checked) =>
                props.setMcpToolSelections((current) => ({
                  ...current,
                  [serverId]: setMcpActionSelection(
                    current[serverId] ?? [],
                    names,
                    checked,
                  ),
                }))
              }
              onToggleAgentWorkspace={(workspaceId) =>
                props.setAgentWorkspaceIds((current) =>
                  current.includes(workspaceId)
                    ? current.filter((id) => id !== workspaceId)
                    : [...current, workspaceId],
                )
              }
              onClearAgentWorkspaces={() => props.setAgentWorkspaceIds([])}
              onSelectAgent={props.onAgentChange}
              onToggleRepository={(workspaceId, nextRepositoryId) =>
                props.setRepositoryIds((current) => {
                  const selected = current[workspaceId] ?? [];
                  return {
                    ...current,
                    [workspaceId]: selected.includes(nextRepositoryId)
                      ? selected.filter((id) => id !== nextRepositoryId)
                      : [...selected, nextRepositoryId],
                  };
                })
              }
              onThinkingLevelChange={props.onThinkingLevelChange}
              onSetupMcp={(server) => void props.setupMcp(server)}
              onOpenSettings={props.setSettingsToolId}
              onSettingsChange={props.updateToolSettings}
              onClose={() => {
                props.setToolsOpen(false);
                props.setSettingsToolId(null);
                props.setMcpDetailServerId(null);
                props.setActivityDetail(null);
              }}
            />,
            document.body,
          )}
        {props.paramsOpen &&
          props.paramsMenuPosition &&
          createPortal(
            <div
              ref={props.paramsPopup}
              style={
                {
                  "--tool-popup-left": `${props.paramsMenuPosition.left}px`,
                  "--tool-popup-bottom": `${props.paramsMenuPosition.bottom}px`,
                  "--tool-popup-width": `${props.paramsMenuPosition.width}px`,
                  "--tool-popup-max-height": `${props.paramsMenuPosition.maxHeight}px`,
                } as CSSProperties
              }
              className="tool-popup-responsive fixed z-[100] overflow-y-auto border border-stone-200 bg-white p-3 shadow-2xl dark:border-white/10 dark:bg-[#222725]"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <div>
                  <strong className="block text-sm">
                    {props.activity === "speech"
                      ? t("Speech options")
                      : props.activity === "audio"
                        ? t("Audio options")
                        : t("AI parameters")}
                  </strong>
                  <span className="text-[11px] text-stone-400">
                    {t("Applied only to this message")}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {props.activeParameterCount > 0 && (
                    <button
                      type="button"
                      className="text-xs text-lumen-600 hover:underline"
                      onClick={() => props.setGenerationParameters({})}
                    >
                      {t("Reset")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="button p-1.5 sm:hidden"
                    onClick={() => props.setParamsOpen(false)}
                    aria-label={t("Close panel")}
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
              {props.activity !== "ask" && (
                <ActivitySettingsPanel
                  activity={props.activity}
                  models={props.activityModels[props.activity] ?? []}
                  settings={props.modelOptionsSettings}
                  allowModelSelection={false}
                  allowAgentChoice={false}
                  onChange={props.updateModelOptions}
                />
              )}
              <div className="grid grid-cols-2 gap-2">
                {props.activity === "ask" &&
                  props.advancedParameterFields.map((field) => (
                    <label key={field.key} className="block">
                      <span className="mb-1 block text-[11px] font-medium text-stone-500">
                        {field.label}
                      </span>
                      <input
                        type="number"
                        min={field.min}
                        max={field.max}
                        step={field.step}
                        value={
                          typeof (
                            props.generationParameters as Record<
                              string,
                              unknown
                            >
                          )[field.key] === "number"
                            ? ((
                                props.generationParameters as Record<
                                  string,
                                  unknown
                                >
                              )[field.key] as number)
                            : ""
                        }
                        placeholder={field.placeholder}
                        onChange={(event) => {
                          const value = event.target.value;
                          props.setGenerationParameters((current) => {
                            const next = {
                              ...current,
                            } as GenerationParameters & Record<string, unknown>;
                            if (value === "") delete next[field.key];
                            else next[field.key] = Number(value);
                            return next;
                          });
                        }}
                        className="field py-1.5 text-xs"
                      />
                    </label>
                  ))}
              </div>
              {props.activity === "ask" && (
                <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg border border-stone-200 p-2 dark:border-white/10">
                  <Toggle
                    className="mt-0.5"
                    checked={props.generationParameters.goal === true}
                    onChange={(event) =>
                      props.setGenerationParameters((current) => ({
                        ...current,
                        ...(event.target.checked
                          ? { goal: true }
                          : { goal: undefined }),
                      }))
                    }
                  />
                  <span>
                    <strong className="block text-xs">{t("Goal mode")}</strong>
                    <span className="block text-[11px] leading-4 text-stone-400">
                      {t(
                        "Use this message as the goal. Describe the outcome and how to verify it. A separate check decides whether to continue, finish, or stop for your input. Runs have a turn limit; Stop ends the run.",
                      )}
                    </span>
                  </span>
                </label>
              )}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}
